import { useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router"
import { NotebookTabs, RefreshCw, Rocket } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"
import { EmptyState, ErrorState, TableSkeleton } from "@/components/states"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { useFormatNumber, useT } from "@/lib/i18n"
import { useScope } from "@/lib/scope"
import { useCell } from "@/lib/session"
import { usePrincipal } from "@/widget/runtime"
import { DataFrame, Stat } from "@/data/frame"
import { useOntology } from "@/workspace/hooks"
import { RefusalNote } from "@/workspace/values"
import { Cell, type ColumnLineage } from "./cell"
import { type Decision, TYPES, WORKBOOK_ID, toDecision, toErpRow, toShopRow, useAllObjects, useCustomerNames } from "./data"
import { useWorkbookDates } from "./dates"
import { mappedFrom } from "./lineage"
import { DEFAULT_SETTINGS, type MatchSettings, type Pair, type Reason, decisionKey, match, needsReview, summarize, weeklyUnmatched } from "./match"
import { ConfidenceBar, CustomerMark, REASON_COLOR, ReasonPill, useWhy } from "./parts"
import { PAYMENTS_WORKBOOK_ID, PAY_TYPES } from "./payments/data"
import { PaymentsWorkbookPage } from "./payments/page"
import { OrderPromoteDialog } from "./promote-dialog"
import { ReviewQueue } from "./review"
import { Sheet } from "./sheet"


/** Data → Workbooks: the workbooks this cell can run. */
export function WorkbooksPage() {
  const t = useT()
  const scope = useScope()
  const { ontology } = useOntology(scope)
  const books = [
    { id: WORKBOOK_ID, title: t("wb.title"), body: t("wb.list.orders"), cells: t("wb.list.cells"), ready: Boolean(ontology?.types.has(TYPES.shop) && ontology.types.has(TYPES.erp)), missing: t("wb.list.notInstalled") },
    {
      id: PAYMENTS_WORKBOOK_ID,
      title: t("pw.title"),
      body: t("pw.list.body"),
      cells: t("pw.list.cells"),
      ready: Boolean(ontology?.types.has(PAY_TYPES.paymob) && ontology.types.has(PAY_TYPES.kashier) && ontology.types.has(PAY_TYPES.invoice)),
      missing: t("pw.list.notInstalled"),
    },
  ]
  return (
    <DataFrame title={t("wb.list.title")} description={t("wb.list.description")} how={["wb.how"]} crumbs={[t("wb.nav")]}>
      <ul className="divide-y divide-border rounded-2xl border border-border">
        {books.map((book) => (
          <li key={book.id}>
            <Link
              to="/$orgId/$environmentId/data/workbooks/$workbookId"
              params={{ ...scope, workbookId: book.id }}
              search={{}}
              className="flex items-start gap-4 px-5 py-4 outline-none hover:bg-muted/40 focus-visible:bg-muted/40"
            >
              <NotebookTabs className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="min-w-0 flex-1 space-y-1">
                <span className="block text-[15px] font-semibold tracking-tight">{book.title}</span>
                <span className="block text-sm text-muted-foreground">{book.body}</span>
                <span className="block text-xs text-subtle-foreground">{book.ready ? book.cells : book.missing}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </DataFrame>
  )
}

/** One route, one page per workbook. */
export function WorkbookPage() {
  const { workbookId } = useParams({ strict: false }) as { workbookId?: string }
  return workbookId === PAYMENTS_WORKBOOK_ID ? <PaymentsWorkbookPage /> : <OrderWorkbookPage />
}

type Filter = Reason | "review" | "all"

function OrderWorkbookPage() {
  const t = useT()
  const scope = useScope()
  const { workbookId } = useParams({ strict: false }) as { workbookId?: string }
  const search = useSearch({ strict: false }) as { focus?: string }
  const { ontology, defs } = useOntology(scope)
  const principal = usePrincipal(scope)
  const actor = principal.status === "ready" ? principal.actor : null
  const installed = Boolean(ontology?.types.has(TYPES.shop) && ontology.types.has(TYPES.erp))
  const shop = useAllObjects(scope, TYPES.shop, installed)
  const erp = useAllObjects(scope, TYPES.erp, installed)
  const hasDecisions = Boolean(ontology?.types.has(TYPES.decision))
  const decisionsList = useAllObjects(scope, TYPES.decision, hasDecisions)
  const decisionsReady = !hasDecisions || (decisionsList.isSuccess && !decisionsList.data.truncated)
  const customers = useCustomerNames(scope, installed && Boolean(ontology?.types.has(TYPES.customer)))
  const dates = useWorkbookDates()
  const [settings, setSettings] = useState<MatchSettings>(DEFAULT_SETTINGS)
  const [filter, setFilter] = useState<Filter>("all")
  const [promoting, setPromoting] = useState(false)
  const queryClient = useQueryClient()
  const { connection } = useCell()
  const formatNumber = useFormatNumber()
  const why = useWhy()

  const shopRows = useMemo(() => (shop.data ? shop.data.items.map(toShopRow) : []), [shop.data])
  const erpRows = useMemo(() => (erp.data ? erp.data.items.map((o) => toErpRow(o, customers.names)) : []), [erp.data, customers.names])
  // The match runs only over complete reads: a refused or capped sheet would invent "missing" pairs.
  // A failed refresh keeps its old data in the cache, so the error states gate too.
  const complete = Boolean(shop.isSuccess && erp.isSuccess && !shop.isError && !erp.isError && !shop.data.truncated && !erp.data.truncated && decisionsReady && !decisionsList.isError && customers.settled)
  const incomplete = !shop.isPending && !erp.isPending && !complete
  const pairs = useMemo(() => (complete ? match(shopRows, erpRows, settings) : []), [complete, shopRows, erpRows, settings])
  const summary = useMemo(() => summarize(pairs, erpRows), [pairs, erpRows])
  const review = useMemo(() => pairs.filter(needsReview).sort((a, b) => a.confidence - b.confidence || a.id.localeCompare(b.id)), [pairs])
  const decisions = useMemo(() => {
    const map = new Map<string, Decision>()
    for (const o of decisionsList.data?.items ?? []) {
      const d = toDecision(o)
      if (d) map.set(d.id, d)
    }
    return map
  }, [decisionsList.data])
  const weekly = useMemo(() => weeklyUnmatched(pairs), [pairs])
  const unreadable = shopRows.filter((s) => s.total === null || s.date === null).length
  const shopLineage = useMemo(() => (defs.data ? mappedFrom(defs.data.artifacts, TYPES.shop) : null), [defs.data])
  const erpLineage = useMemo(() => (defs.data ? mappedFrom(defs.data.artifacts, TYPES.erp) : null), [defs.data])
  const loading = shop.isPending || erp.isPending
  const readAt = Math.min(shop.data?.readAt ?? 0, erp.data?.readAt ?? 0)

  // A link from Business mode lands on the review queue.
  useEffect(() => {
    if (search.focus === "review" && !loading) document.getElementById("cell-review")?.scrollIntoView({ block: "start" })
  }, [search.focus, loading])

  if (workbookId !== WORKBOOK_ID)
    return (
      <DataFrame title={t("wb.nav")} how={["wb.how"]} crumbs={[t("wb.nav")]}>
        <EmptyState icon={<NotebookTabs />} title={t("wb.unknown")} />
      </DataFrame>
    )

  const from = (lineage: typeof shopLineage, property: string, fallback: string) =>
    lineage?.expressions.get(property) ? `${lineage.system} · ${lineage.dataset} · ${lineage.expressions.get(property)}` : fallback
  const shopChain = [`${shopLineage?.system ?? "Shopify"} webhook · orders/create`, shopLineage?.mapping ?? "shopify.orders", TYPES.shop, t("wb.cell.shopify")]
  const erpChain = [`${erpLineage?.system ?? "ERPNext"} · Sales Order`, erpLineage?.mapping ?? "erpnext.norn_core", TYPES.erp, t("wb.cell.erp")]
  const rules = t("wb.rules.text", { amount: settings.amountTolerance < 0.01 ? t("wb.rules.exact") : `±${settings.amountTolerance}`, days: settings.dayTolerance })
  const unmatched = summary.byReason.missing_in_erp + summary.byReason.missing_in_shopify + summary.byReason.amount_mismatch + summary.byReason.duplicate
  const filtered = filter === "all" ? pairs : filter === "review" ? pairs.filter((p) => p.reason === "matched" && needsReview(p)) : pairs.filter((p) => p.reason === filter && (filter !== "matched" || !needsReview(p)))
  // Disagreements first, least certain first: the rows that need a person lead the sheet.
  const shown = [...filtered].sort((a, b) => Number(needsReview(b)) - Number(needsReview(a)) || a.confidence - b.confidence)

  const matchLineage: ColumnLineage[] = [
    { column: t("wb.col.verdict"), from: "rules 1–3 (this cell)", means: t("wb.mean.verdict") },
    { column: t("wb.col.confidence"), from: "rule × tolerance", means: t("wb.mean.confidence") },
    { column: t("wb.col.delta"), from: "total(Shopify) − total(ERPNext)", means: t("wb.mean.delta") },
    { column: t("wb.col.days"), from: "day(Shopify) − order_date(ERPNext)", means: t("wb.mean.days") },
    { column: t("wb.col.customer"), from: "lower(email(Shopify)) = lower(email(ERPNext customer))", means: t("wb.mean.customerSignal") },
  ]

  return (
    <DataFrame
      title={t("wb.title")}
      description={t("wb.description")}
      how={["wb.how"]}
      width="full"
      crumbs={[
        <Link key="wb" to="/$orgId/$environmentId/data/workbooks" params={scope} search={{}}>
          {t("wb.nav")}
        </Link>,
        t("wb.title"),
      ]}
      actions={
        <>
          <button
            type="button"
            onClick={() => void queryClient.invalidateQueries({ queryKey: ["wb-all", connection.baseUrl] })}
            disabled={shop.isFetching || erp.isFetching}
            className="inline-flex h-8 items-center gap-1.5 rounded-full border border-border px-3 text-xs outline-none hover:bg-muted/60 focus-visible:ring-3 focus-visible:ring-ring/30 disabled:opacity-60"
          >
            <RefreshCw className={shop.isFetching || erp.isFetching ? "size-3.5 animate-spin" : "size-3.5"} aria-hidden /> {t("wb.run")}
          </button>
          <button
            type="button"
            onClick={() => setPromoting(true)}
            disabled={!installed || !complete}
            className="inline-flex h-8 items-center gap-1.5 rounded-full bg-foreground px-3 text-xs font-medium text-background outline-none hover:opacity-90 focus-visible:ring-3 focus-visible:ring-ring/30 disabled:opacity-50"
          >
            <Rocket className="size-3.5" aria-hidden /> {t("wb.promote.button")}
          </button>
        </>
      }
    >
      {defs.isError ? (
        <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
      ) : !ontology ? (
        <TableSkeleton rows={6} columns={5} />
      ) : !installed ? (
        <EmptyState icon={<NotebookTabs />} title={t("wb.notInstalled")}>
          {t("wb.notInstalledBody")}
        </EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 border-y border-border py-4 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label={t("wb.stat.shopify")} value={loading ? "…" : formatNumber(shopRows.length)} />
            <Stat label={t("wb.stat.erp")} value={loading ? "…" : formatNumber(erpRows.length - summary.outOfWindow)} hint={summary.outOfWindow ? t("wb.stat.outside", { count: summary.outOfWindow }) : undefined} />
            <Stat label={t("wb.reason.matched")} value={loading ? "…" : formatNumber(summary.clean)} />
            <Stat label={t("wb.stat.review")} value={loading ? "…" : formatNumber(review.filter((p) => !decisions.has(decisionKey(p))).length)} hint={t("wb.stat.decided", { count: review.filter((p) => decisions.has(decisionKey(p))).length })} />
            <Stat label={t("wb.stat.unmatched")} value={loading ? "…" : formatNumber(unmatched)} hint={pairs.length ? `${Math.round((unmatched / pairs.length) * 1000) / 10}%` : undefined} />
            <Stat label={t("wb.stat.read")} value={<span className="text-sm font-medium">{readAt ? new Date(readAt).toLocaleTimeString() : "…"}</span>} hint={t("wb.stat.live")} />
          </div>

          {incomplete ? (
            <p role="alert" className="rounded-xl border border-dashed border-destructive/50 px-4 py-3 text-sm">
              {shop.data?.truncated || erp.data?.truncated ? t("wb.incomplete.truncated") : t("wb.incomplete.refused")}
            </p>
          ) : null}

          <div className="pt-2">
            <Cell
              index={1}
              kind="source"
              title={t("wb.cell.shopify")}
              summary={shop.data ? t("wb.sheet.rows", { count: shopRows.length }) : undefined}
              chain={shopChain}
              lineage={[
                { column: t("wb.col.order"), from: from(shopLineage, "order_name", "orders.name"), means: t("wb.mean.shopOrder") },
                { column: t("wb.col.placed"), from: from(shopLineage, "created_at", "orders.created_at"), means: t("wb.mean.placed") },
                { column: t("wb.col.total"), from: from(shopLineage, "total_price", "orders.total_price"), means: t("wb.mean.shopTotal") },
                { column: t("wb.col.status"), from: from(shopLineage, "financial_status", "orders.financial_status"), means: t("wb.mean.financial") },
                { column: t("wb.col.customer"), from: from(shopLineage, "customer_email", "orders.email"), means: t("wb.mean.email") },
                { column: t("wb.col.reference"), from: from(shopLineage, "erp_reference", "orders.source_identifier"), means: t("wb.mean.reference") },
              ]}
            >
              {shop.isError ? (
                <RefusalNote error={shop.error} title={t("wb.readRefused")} />
              ) : shop.isPending ? (
                <TableSkeleton rows={6} columns={6} />
              ) : (
                <Sheet
                  label={t("wb.cell.shopify")}
                  rows={shopRows}
                  rowKey={(r) => r.key}
                  empty={t("wb.empty.shopify")}
                  columns={[
                    { id: "name", label: t("wb.col.order"), value: (r) => r.name, width: "7rem" },
                    { id: "placed", label: t("wb.col.placed"), value: (r) => r.createdAt, display: (r) => dates.stamp(r.createdAt), width: "11.5rem" },
                    { id: "total", label: t("wb.col.total"), value: (r) => r.totalText, numeric: true, width: "8rem" },
                    { id: "cur", label: t("wb.col.currency"), value: (r) => r.currency, width: "5rem" },
                    { id: "status", label: t("wb.col.status"), value: (r) => r.status, width: "6.5rem" },
                    { id: "email", label: t("wb.col.customer"), value: (r) => (r.emailHidden ? t("wb.hidden") : r.email) },
                    { id: "ref", label: t("wb.col.reference"), value: (r) => r.reference, mono: true, width: "11rem" },
                  ]}
                />
              )}
            </Cell>

            <Cell
              index={2}
              kind="source"
              title={t("wb.cell.erp")}
              summary={erp.data ? t("wb.sheet.rows", { count: erpRows.length }) : undefined}
              chain={erpChain}
              lineage={[
                { column: t("wb.col.order"), from: from(erpLineage, "order_number", "Sales Order.name"), means: t("wb.mean.erpOrder") },
                { column: t("wb.col.date"), from: from(erpLineage, "order_date", "Sales Order.transaction_date"), means: t("wb.mean.erpDate") },
                { column: t("wb.col.total"), from: from(erpLineage, "total_amount", "Sales Order.grand_total"), means: t("wb.mean.erpTotal") },
                { column: t("wb.col.status"), from: "Sales Order.docstatus → status", means: t("wb.mean.erpStatus") },
                { column: t("wb.col.customer"), from: `${from(erpLineage, "showcaselive__customer", "Sales Order.customer")} → ${TYPES.customer}.display_name`, means: t("wb.mean.erpCustomer") },
                { column: t("wb.col.email"), from: `${TYPES.customer}.showcaselive__email ← Customer.email_id`, means: t("wb.mean.email") },
              ]}
            >
              {erp.isError ? (
                <RefusalNote error={erp.error} title={t("wb.readRefused")} />
              ) : erp.isPending ? (
                <TableSkeleton rows={6} columns={5} />
              ) : (
                <Sheet
                  label={t("wb.cell.erp")}
                  rows={erpRows}
                  rowKey={(r) => r.key}
                  empty={t("wb.empty.erp")}
                  columns={[
                    { id: "number", label: t("wb.col.order"), value: (r) => r.number, mono: true, width: "11rem" },
                    { id: "date", label: t("wb.col.date"), value: (r) => r.date, display: (r) => dates.day(r.date), width: "8rem" },
                    { id: "total", label: t("wb.col.total"), value: (r) => r.total, numeric: true, width: "8rem" },
                    { id: "cur", label: t("wb.col.currency"), value: (r) => r.currency, width: "5rem" },
                    { id: "status", label: t("wb.col.status"), value: (r) => r.status, width: "6.5rem" },
                    { id: "customer", label: t("wb.col.customer"), value: (r) => (r.customerHidden ? t("wb.hidden") : (r.customer ?? r.customerKey)), width: "13rem" },
                    { id: "email", label: t("wb.col.email"), value: (r) => (r.customerEmailHidden ? t("wb.hidden") : r.customerEmail) },
                  ]}
                />
              )}
            </Cell>

            <Cell
              index={3}
              kind="transform"
              title={t("wb.cell.normalize")}
              summary={unreadable ? t("wb.normalize.unreadable", { count: unreadable }) : t("wb.normalize.allRead")}
              chain={[t("wb.cell.shopify"), t("wb.cell.normalize")]}
              lineage={[
                { column: t("wb.col.amount"), from: "number(total_price)", means: t("wb.mean.amount") },
                { column: t("wb.col.day"), from: "date part of created_at (store offset)", means: t("wb.mean.day") },
              ]}
            >
              <p className="max-w-3xl text-xs text-muted-foreground">{t("wb.normalize.body")}</p>
              <Sheet
                label={t("wb.cell.normalize")}
                rows={shopRows}
                rowKey={(r) => r.key}
                empty={t("wb.empty.shopify")}
                rowTone={(r) => (r.total === null || r.date === null ? "bad" : undefined)}
                columns={[
                  { id: "name", label: t("wb.col.order"), value: (r) => r.name, width: "7rem" },
                  { id: "text", label: t("wb.col.shopifyText"), value: (r) => r.totalText, mono: true, width: "9rem" },
                  { id: "amount", label: t("wb.col.amount"), value: (r) => r.total, numeric: true, width: "8rem" },
                  { id: "day", label: t("wb.col.day"), value: (r) => r.date, display: (r) => dates.day(r.date), width: "8rem" },
                  { id: "ref", label: t("wb.col.reference"), value: (r) => r.reference, mono: true },
                ]}
              />
            </Cell>

            <Cell
              index={4}
              kind="match"
              title={t("wb.cell.match")}
              summary={t("wb.match.summary", { pairs: formatNumber(pairs.length), review: formatNumber(summary.review) })}
              chain={[t("wb.cell.normalize"), t("wb.cell.erp"), t("wb.cell.match")]}
              lineage={matchLineage}
            >
              <ol className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
                <li className="rounded-lg border border-border px-3 py-2">
                  <span className="font-medium text-foreground">1 · {t("wb.rule.reference")}</span> {t("wb.rule.referenceBody")}
                </li>
                <li className="rounded-lg border border-border px-3 py-2">
                  <span className="font-medium text-foreground">2 · {t("wb.rule.amountDate")}</span>{" "}
                  {t("wb.rule.amountDateBody", { days: settings.dayTolerance })}
                  <span className="mt-2 flex flex-wrap items-center gap-2">
                    <label className="inline-flex items-center gap-1">
                      {t("wb.rule.days")}
                      <select
                        value={settings.dayTolerance}
                        onChange={(e) => setSettings((s) => ({ ...s, dayTolerance: Number(e.target.value) }))}
                        className="h-6 rounded-md border border-input bg-background px-1 text-xs"
                      >
                        {[0, 1, 2, 3].map((d) => (
                          <option key={d} value={d}>
                            ±{d}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="inline-flex items-center gap-1">
                      {t("wb.rule.amount")}
                      <select
                        value={settings.amountTolerance}
                        onChange={(e) => setSettings((s) => ({ ...s, amountTolerance: Number(e.target.value) }))}
                        className="h-6 rounded-md border border-input bg-background px-1 text-xs"
                      >
                        <option value={0.005}>{t("wb.rules.exact")}</option>
                        <option value={1}>±1</option>
                        <option value={5}>±5</option>
                      </select>
                    </label>
                  </span>
                </li>
                <li className="rounded-lg border border-border px-3 py-2">
                  <span className="font-medium text-foreground">3 · {t("wb.rule.leftover")}</span> {t("wb.rule.leftoverBody")}
                </li>
              </ol>
              <div role="group" aria-label={t("wb.match.filter")} className="flex flex-wrap gap-1.5">
                {(
                  [
                    ["all", pairs.length],
                    ["matched", summary.clean],
                    ["review", summary.fuzzy],
                    ["amount_mismatch", summary.byReason.amount_mismatch],
                    ["duplicate", summary.byReason.duplicate],
                    ["missing_in_erp", summary.byReason.missing_in_erp],
                    ["missing_in_shopify", summary.byReason.missing_in_shopify],
                  ] as const
                ).map(([id, count]) => (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={filter === id}
                    onClick={() => setFilter(id)}
                    className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs outline-none focus-visible:ring-3 focus-visible:ring-ring/30 ${filter === id ? "border-foreground/40 bg-muted" : "border-border hover:bg-muted/50"}`}
                  >
                    {id !== "all" ? <span className="size-1.5 rounded-full" style={{ background: REASON_COLOR[id] }} aria-hidden /> : null}
                    {id === "all" ? t("wb.match.all") : id === "review" ? t("wb.reason.fuzzy") : t(`wb.reason.${id}` as const)}
                    <span className="tabular-nums text-muted-foreground">{formatNumber(count)}</span>
                  </button>
                ))}
              </div>
              {loading ? (
                <TableSkeleton rows={6} columns={6} />
              ) : (
                <Sheet
                  label={t("wb.cell.match")}
                  rows={shown}
                  rowKey={(p) => p.id}
                  empty={t("wb.empty.match")}
                  rowTone={(p) => (p.reason === "matched" ? (needsReview(p) ? "warn" : undefined) : "bad")}
                  columns={[
                    { id: "verdict", label: t("wb.col.verdict"), value: (p) => p.reason, render: (p: Pair) => <ReasonPill reason={p.reason} rule={p.rule} review={needsReview(p)} />, width: "9.5rem" },
                    { id: "conf", label: t("wb.col.confidence"), value: (p) => p.confidence, render: (p: Pair) => <ConfidenceBar value={p.confidence} />, width: "6.5rem" },
                    { id: "shop", label: t("wb.side.shopify"), value: (p) => p.shop?.name ?? null, width: "7rem" },
                    { id: "erp", label: t("wb.side.erp"), value: (p) => p.erp?.number ?? null, mono: true, width: "11rem" },
                    { id: "delta", label: t("wb.col.delta"), value: (p) => p.delta, numeric: true, width: "6rem" },
                    { id: "days", label: t("wb.col.days"), value: (p) => p.dayGap, numeric: true, width: "5rem" },
                    { id: "customer", label: t("wb.col.customer"), value: (p) => p.customer, render: (p: Pair) => <CustomerMark signal={p.customer} oneSided={!p.shop || !p.erp} />, width: "7rem" },
                    { id: "why", label: t("wb.col.why"), value: (p) => why(p) },
                  ]}
                />
              )}
            </Cell>

            <Cell
              id="cell-review"
              index={5}
              kind="review"
              title={t("wb.cell.review")}
              summary={t("wb.review.summary", { open: review.filter((p) => !decisions.has(decisionKey(p))).length, decided: review.filter((p) => decisions.has(decisionKey(p))).length })}
              chain={[t("wb.cell.match"), t("wb.cell.review"), `${TYPES.decision} (ledger)`]}
              lineage={[
                { column: t("wb.col.verdict"), from: "recon.order_match.decide → recon.OrderMatchDecision.outcome", means: t("wb.mean.decision") },
                { column: t("wb.review.receipt"), from: "ActionAccepted.receipt.seq", means: t("wb.mean.receipt") },
              ]}
            >
              {decisionsList.isError ? <RefusalNote error={decisionsList.error} title={t("wb.readRefused")} /> : null}
              {loading || (hasDecisions && decisionsList.isPending) ? <TableSkeleton rows={5} columns={3} /> : complete ? <ReviewQueue scope={scope} pairs={review} decisions={decisions} actor={actor} /> : null}
            </Cell>

            <Cell
              index={6}
              kind="chart"
              title={t("wb.cell.chart")}
              summary={t("wb.chart.summary", { weeks: weekly.length })}
              chain={[t("wb.cell.match"), t("wb.cell.chart")]}
              lineage={[{ column: t("wb.chart.week"), from: "Monday of day(Shopify) else order_date(ERPNext)", means: t("wb.mean.week") }]}
            >
              {weekly.length ? (
                <ChartContainer className="h-60">
                  <BarChart data={weekly} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="var(--border)" />
                    <XAxis dataKey="week" tickLine={false} axisLine={false} fontSize={11} stroke="var(--muted-foreground)" tickFormatter={(week: string) => dates.day(week) ?? week} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={11} stroke="var(--muted-foreground)" />
                    <ChartTooltip cursor={{ fill: "var(--muted)" }} content={<ChartTooltipContent />} />
                    {(["review", "amount_mismatch", "duplicate", "missing_in_erp", "missing_in_shopify"] as const).map((k) => (
                      <Bar key={k} dataKey={k} stackId="u" name={k === "review" ? t("wb.reason.fuzzy") : t(`wb.reason.${k}` as const)} fill={REASON_COLOR[k]} radius={0} />
                    ))}
                  </BarChart>
                </ChartContainer>
              ) : (
                <p className="text-sm text-muted-foreground">{t("wb.empty.chart")}</p>
              )}
            </Cell>
          </div>
          {promoting ? (
            <OrderPromoteDialog
              scope={scope}
              actor={actor}
              counts={{ matched: summary.clean, needsReview: review.filter((p) => !decisions.has(decisionKey(p))).length, unmatched }}
              rules={rules}
              ready={complete}
              onClose={() => setPromoting(false)}
            />
          ) : null}
        </>
      )}
    </DataFrame>
  )
}

/** Navigate helper for other surfaces: open the workbook at its review queue. */
export function useOpenWorkbookReview() {
  const navigate = useNavigate()
  const scope = useScope()
  return () => void navigate({ to: "/$orgId/$environmentId/data/workbooks/$workbookId", params: { ...scope, workbookId: WORKBOOK_ID }, search: { focus: "review" } })
}
